import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { Button } from "@volca/ui/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@volca/ui/components/ui/collapsible";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { dayjs } from "@/core/utils/date";
import { MetaList } from "@/profitability/meta-list";
import {
  useArchiveReport,
  useArchivedReports,
} from "@/profitability/report-query";
import type { ProfitabilityReport } from "@/profitability/types";
import { reportError } from "@/profitability/utils";

const ArchivedTemplate = ({
  businessId,
  report,
}: {
  businessId: string;
  report: ProfitabilityReport;
}) => {
  const restore = useArchiveReport(businessId, "restore");
  const runs = report.run_count ?? 0;
  return (
    <li>
      <Stack
        direction="row"
        align="center"
        justify="between"
        gap={4}
        className="py-3"
      >
        <Stack gap={0} align="start" className="min-w-0">
          <Text className="truncate font-medium">{report.name}</Text>
          <Text variant="caption" tone="muted">
            <MetaList>
              {report.archived_at &&
                `Archived ${dayjs(report.archived_at).format("MMM D, YYYY")}`}
              {`${runs} ${runs === 1 ? "run" : "runs"} kept`}
            </MetaList>
          </Text>
        </Stack>
        <Button
          size="sm"
          variant="secondary"
          loading={restore.isPending}
          onClick={() =>
            restore.mutate(report.id, {
              onSuccess: () => toast.success(`${report.name} was restored`),
              onError: (error) =>
                toast.error("The agent could not be restored", {
                  description: reportError(error),
                }),
            })
          }
        >
          Restore
        </Button>
      </Stack>
    </li>
  );
};

/**
 * The templates an owner archived (RPT-008), below the active ones and closed
 * by default: each keeps its runs and results, and comes back with Restore.
 * Nothing shows while there are none.
 */
export const ArchivedTemplates = ({ businessId }: { businessId: string }) => {
  const archived = useArchivedReports(businessId);
  const reports = archived.data?.pages.flatMap((page) => page.reports) ?? [];
  if (!reports.length) return null;
  return (
    <Collapsible className="w-full">
      <CollapsibleTrigger
        render={<Button variant="ghost" size="sm" className="group" />}
      >
        <CaretRightIcon
          aria-hidden
          className="transition-transform group-data-[panel-open]:rotate-90"
        />
        Archived agents ({reports.length})
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul
          aria-label="Archived agents"
          className="divide-y divide-line border-y border-line"
        >
          {reports.map((report) => (
            <ArchivedTemplate
              key={report.id}
              businessId={businessId}
              report={report}
            />
          ))}
        </ul>
        {archived.hasNextPage && (
          <Button
            variant="ghost"
            loading={archived.isFetchingNextPage}
            onClick={() => void archived.fetchNextPage()}
          >
            Show more archived agents
          </Button>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};
